import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SaviBusinessJsonLd from "../../components/SaviBusinessJsonLd";
import SaviQuiz from "../../components/SaviQuiz";
import { ArrowRight, Sun, Coffee, Dumbbell, Moon, Mountain, Users, Wifi, Monitor } from "lucide-react";
import { pic } from "../../lib/pic";

const SoberColivingPage = () => {
  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Sober Coliving in Spain | Savi Coliving Valencia</title>
        <meta name="description" content="A sober coliving space in the mountains of Valencia. No alcohol, no party culture. Built for focus, clarity, and real connection." />
        <link rel="canonical" href="https://www.savicoliving.com/sober-coliving" />
        <meta property="og:title" content="Sober Coliving in Spain | Savi Coliving" />
        <meta property="og:description" content="Most colivings are built around drinking. This one isn't. A sober coliving in the mountains of Valencia." />
        <meta property="og:image" content="https://static.wixstatic.com/media/447ef7_c4f040f251774723aaf22ea2f5874e83~mv2.jpg" />
      </Helmet>
      <SaviBusinessJsonLd id="sober-coliving" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FBF7F1]/95 backdrop-blur-lg border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png" alt="Savi" className="h-7 w-auto" />
            <span className="text-base tracking-wider text-[#C89B7B]"><span style={{fontFamily: 'Cinzel, serif'}}>SAVI</span> <span style={{fontFamily: "'Cormorant Garamond', serif"}}>Coliving</span></span>
          </Link>
          <Link to="/" className="text-sm text-[#C85A38] font-medium hover:underline">View the house →</Link>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-14">
        <div className="relative h-[75vh] overflow-hidden">
          <img {...pic("mountain-view")} fetchpriority="high" decoding="async" alt="Sober coliving in the mountains of Valencia Spain" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2E2A26] via-[#2E2A26]/55 to-[#2E2A26]/30" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-5 max-w-4xl leading-tight" style={{textShadow: '0 2px 12px rgba(0,0,0,0.5)'}}>
              Most colivings are built<br className="hidden sm:block" /> around drinking. This one isn't.
            </h1>
            <p className="text-white/60 text-base sm:text-lg max-w-xl mb-8" style={{textShadow: '0 2px 8px rgba(0,0,0,0.4)'}}>
              A <strong className="text-white/80">sober coliving</strong> space in the mountains of Valencia for people who want clarity, focus, and better people around them.
            </p>
            <Link to="/contact">
              <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-10 py-4 text-sm font-medium tracking-wide transition-all">
                Apply to stay <ArrowRight className="inline ml-2 w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* WHAT IS SOBER COLIVING */}
      <section className="py-14 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-6">What is sober coliving?</h2>
          <p className="text-[#2E2A26]/70 text-base leading-relaxed mb-4">
            <strong className="text-[#2E2A26]">Sober coliving</strong> is a shared living environment where alcohol and recreational drugs aren't part of the equation. People live and work together — usually for one to three months — in a space that's designed around focus, health, and genuine connection.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            It's not a new concept, but it's becoming a category because more people are realising the same thing: the default social environment for adults is built around drinking, and that doesn't work for everyone.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            Traditional coliving — the kind you find in Lisbon, Bali, Barcelona — is usually fine. Nice apartments, shared kitchens, maybe a coworking space. But the culture is almost always the same: drinks on the rooftop, beers after work, "community events" that are really just excuses to drink. If you don't participate, you're the odd one out.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            <strong className="text-[#2E2A26]/80">Sober coliving flips that.</strong> The baseline is clarity. The social environment doesn't revolve around substances. People connect through meals, hikes, conversations, skill shares — not happy hours. The result is a house full of people who are actually present. And that changes everything.
          </p>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="py-14 px-4 bg-[#F4ECE2]">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-6">Why this matters right now</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-6">
            Something shifted in the last few years. A growing number of high-functioning people — founders, creatives, remote workers — are quietly stepping away from alcohol. Not because they have a "problem." Because they've done the maths on what it costs them in focus, energy, and time.
          </p>
          <div className="space-y-4">
            {[
              { title: "The burnout loop", text: "Work hard, drink to unwind, wake up foggy, repeat. Most remote workers know this cycle. Sober coliving breaks it by removing the trigger from the environment." },
              { title: "Lonely despite being 'social'", text: "Alcohol creates the illusion of connection. Sober environments force the real thing — which is harder at first, and infinitely better after." },
              { title: "No structure, no progress", text: "Working from home or a café with no routine is a recipe for spinning wheels. An intentional coliving space builds rhythm into your day without forcing it." },
              { title: "The wrong people around you", text: "You're the average of the five people you spend the most time with. In most colivings, those people are transient backpackers. In a sober coliving, they're builders." }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5">
                <p className="text-[#2E2A26] text-sm font-medium mb-1">{item.title}</p>
                <p className="text-[#2E2A26]/60 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT SAVI IS AND ISN'T */}
      <section className="py-14 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-6">What Savi is (and isn't)</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-6">
            Let's be clear upfront, because people get confused:
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-[#2E2A26] rounded-xl p-5">
              <p className="text-white/90 text-sm font-medium mb-3">Savi is NOT:</p>
              <div className="space-y-2">
                {["A rehab facility", "A wellness retreat", "A therapy programme", "A party house with rules", "A hostel with branding"].map((item, i) => (
                  <p key={i} className="text-white/40 text-sm">— {item}</p>
                ))}
              </div>
            </div>
            <div className="bg-[#C85A38]/5 border border-[#C85A38]/10 rounded-xl p-5">
              <p className="text-[#2E2A26] text-sm font-medium mb-3">Savi IS:</p>
              <div className="space-y-2">
                {["A private villa for focused people", "A no-alcohol environment by design", "A coworking + coliving space", "A community of up to 16 residents", "A place to do your best work"].map((item, i) => (
                  <p key={i} className="text-[#2E2A26]/60 text-sm">+ {item}</p>
                ))}
              </div>
            </div>
          </div>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            Think of it as a high-functioning living environment for people who want to operate at their best. Some residents are years into sobriety. Some are "sober-curious." Some just don't like the version of themselves that shows up after a few drinks. All of them want the same thing: an environment that supports what they're trying to build — not one that undermines it every evening.
          </p>
        </div>
      </section>

      {/* DAILY LIFE */}
      <section className="py-14 px-4 bg-[#F4ECE2]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-6">What a day actually looks like</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-8">
            Not a brochure version. This is what actually happens.
          </p>
          <div className="space-y-4">
            {[
              { icon: Sun, time: "7–9am", title: "Morning", text: "People wake up when they want. Coffee on the terrace. The dogs — Viki, Lluna, and Jenny — are already doing laps. Some people meditate. Most just sit with their coffee and watch the mountains. No alarms, no pressure." },
              { icon: Coffee, time: "9am–1pm", title: "Deep work", text: "The coworking space fills up. Dual screens, Starlink WiFi (500Mbps), standing desks. It's quiet. People are actually working — not pretending to work while scrolling Instagram. Skill shares some mornings. Accountability sessions weekly." },
              { icon: Dumbbell, time: "Afternoon", title: "Move", text: "Gym, pool, cold plunge, a hike in the Sierra Calderona. Some people drive into Valencia (25 minutes). Others just need space. The afternoons are flexible — work if you want, rest if you need to." },
              { icon: Moon, time: "Evening", title: "Together", text: "Someone cooks. Everyone eats on the roof terrace. The sun sets behind the mountains. Conversations go somewhere real — because nobody's three beers deep and performing. Bed by 11. And tomorrow, you wake up clear." }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#C85A38]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#C85A38]" />
                </div>
                <div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <p className="text-[#2E2A26] text-sm font-medium">{item.title}</p>
                    <p className="text-[#C85A38] text-xs">{item.time}</p>
                  </div>
                  <p className="text-[#2E2A26]/60 text-sm leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-14 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-6">Who comes here</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-6">
            There's no single "type." But if you recognise yourself in any of these, you'll probably fit:
          </p>
          <div className="space-y-3 mb-6">
            {[
              "You're building something — a company, a creative project, a new career — and you need an environment that supports that",
              "You've quit drinking, or you're thinking about it, and you don't want to be the 'weird one' in a social house",
              "You're a remote worker who's tired of working alone in apartments and cafés",
              "You want real friendships, not surface-level 'where are you from?' conversations over beer",
              "You want structure without rigidity — mornings that work, evenings that recharge",
              "You've done the digital nomad thing and you're looking for something with more substance"
            ].map((item, i) => (
              <div key={i} className="flex gap-3">
                <span className="text-[#C85A38] text-sm mt-0.5 flex-shrink-0">→</span>
                <p className="text-[#2E2A26]/60 text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-[#2E2A26]/40 text-sm italic">
            We review every application. Not everyone's the right fit — and that's the point. The community only works because we're intentional about who's in it.
          </p>
        </div>
      </section>

      {/* THE SPACE */}
      <section className="py-14 px-4 bg-[#F4ECE2]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-6">The space</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {[
              { icon: Mountain, label: "Mountain villa", detail: "Private property, Sierra Calderona" },
              { icon: Monitor, label: "Coworking", detail: "Dual screens, standing desks" },
              { icon: Wifi, label: "500Mbps WiFi", detail: "Starlink — fast and reliable" },
              { icon: Dumbbell, label: "Gym + pool", detail: "On-site, no membership needed" },
              { icon: Users, label: "up to 16 residents", detail: "Small, curated, intentional" },
              { icon: Coffee, label: "Full kitchen", detail: "Cook together, eat together" }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#C85A38]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#C85A38]" />
                </div>
                <div>
                  <p className="text-[#2E2A26] text-sm font-medium">{item.label}</p>
                  <p className="text-[#2E2A26]/40 text-xs">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTOS */}
      <section className="px-2 sm:px-4 py-2">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-2">
          <img {...pic("villa-front", { sizes: "(min-width: 1024px) 256px, 50vw", fallback: 480 })} loading="lazy" decoding="async" alt="Savi villa exterior" className="w-full h-40 sm:h-48 object-cover rounded-lg" />
          <img {...pic("coworking", { sizes: "(min-width: 1024px) 256px, 50vw", fallback: 480 })} loading="lazy" decoding="async" alt="Coworking space at Savi" className="w-full h-40 sm:h-48 object-cover rounded-lg" />
          <img {...pic("room-suite", { sizes: "(min-width: 1024px) 256px, 50vw", fallback: 480 })} loading="lazy" decoding="async" alt="Room interior at Savi" className="w-full h-40 sm:h-48 object-cover rounded-lg" />
          <img {...pic("mountain-view", { sizes: "(min-width: 1024px) 256px, 50vw", fallback: 480 })} loading="lazy" decoding="async" alt="Mountain views from Savi" className="w-full h-40 sm:h-48 object-cover rounded-lg" />
        </div>
      </section>

      {/* LOCATION */}
      <section className="py-14 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-6">Location</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            Savi is in the Sierra Calderona mountains, just outside Náquera — a quiet village 25 minutes from <strong className="text-[#2E2A26]/80">Valencia, Spain</strong>. It's close enough to access everything the city offers, far enough that you actually focus.
          </p>
          <div className="bg-[#F4ECE2] rounded-xl p-5 space-y-2">
            {[
              "25 minutes to Valencia city centre",
              "15 minutes to the beach at Puçol",
              "35 minutes to Valencia airport",
              "Hiking trails from the front door",
              "Year-round Mediterranean climate"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-[#C85A38]" />
                <p className="text-[#2E2A26]/60 text-sm">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mt-4">
            If you're looking for <strong className="text-[#2E2A26]/80">sober coliving in Spain</strong> — or anywhere in <strong className="text-[#2E2A26]/80">Europe</strong> — this is one of the only purpose-built options that exists. Most "alcohol-free" colivings are just regular colivings with a loose policy. Savi was designed from the ground up as a <strong className="text-[#2E2A26]/80">coliving without alcohol</strong>.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 px-4 bg-[#F4ECE2]">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-8">Sober coliving — FAQ</h2>
          <div className="space-y-4">
            {[
              { q: "Do I have to be sober to live at Savi?", a: "You don't need to identify as 'sober.' You just need to be comfortable in a no-alcohol environment. No drinking on the property, no exceptions. What you do on your own time outside Savi is your business." },
              { q: "Is this a recovery house?", a: "No. Savi is not a treatment facility, rehab, or recovery programme. There are no therapists, no group therapy, no 12 steps. It's a coliving space for people who choose not to drink — for whatever reason." },
              { q: "What if I'm not sure if I want to quit drinking?", a: "That's fine. A lot of people come here 'sober-curious.' They want to try living without alcohol for a month and see how it feels. Most of them extend." },
              { q: "How long can I stay?", a: "Minimum one month. Most people stay 1-3 months. Some stay longer. We're flexible." },
              { q: "How much does it cost?", a: "Rooms from €1,200/month. Everything included — coworking, WiFi, gym, pool, utilities, community. No hidden fees." },
              { q: "Is the community actually good?", a: "We review every application specifically to make sure it is. We turn down people who aren't the right fit. The community works because it's intentional — not because we got lucky." }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5">
                <h3 className="text-[#2E2A26] text-sm font-medium mb-2">{item.q}</h3>
                <p className="text-[#2E2A26]/60 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FURTHER READING */}
      <section className="py-14 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl text-[#2E2A26] mb-6">Further reading</h2>
          <div className="space-y-3">
            {[
              { title: "Why Most Colivings Don't Work", url: "/why-most-colivings-dont-work", desc: "The structural problems with traditional coliving — and what sober coliving fixes." },
              { title: "Sober Living for Remote Workers", url: "/sober-living-remote-workers", desc: "What happens when you remove alcohol from the digital nomad equation." },
              { title: "Best Sober Coliving Spaces in Europe", url: "/best-sober-coliving-europe", desc: "The short list of places that actually exist. Spoiler: it's short." },
              { title: "Is Sober Coliving Worth It?", url: "/is-sober-coliving-worth-it", desc: "Pros, cons, and who it's actually for." },
              { title: "Coliving vs. Retreat vs. Sober Coliving", url: "/coliving-vs-retreat-vs-sober-coliving", desc: "Different models, different outcomes. A clear comparison." },
              { title: "What Is Sober Curious?", url: "/sober-curious-guide", desc: "The movement that's changing how people think about alcohol." },
              { title: "Digital Nomad Coliving in Spain", url: "/digital-nomad-coliving-spain", desc: "What actually works for remote workers in Spain." },
              { title: "Alcohol-Free Travel", url: "/alcohol-free-travel", desc: "How to travel sober and enjoy it more." },
              { title: "Living Abroad Without Drinking", url: "/living-abroad-without-drinking", desc: "The honest guide to expat life without alcohol." },
              { title: "Coliving for Entrepreneurs", url: "/coliving-for-entrepreneurs", desc: "Why founders are choosing coliving over apartments." },
              { title: "Finding Community as a Remote Worker", url: "/how-to-find-community-as-remote-worker", desc: "The loneliness problem — and the real solution." },
              { title: "What Is Intentional Living?", url: "/what-is-intentional-living", desc: "Not what Instagram tells you. Here's what it actually means." },
              { title: "Deep Work Environment", url: "/deep-work-environment", desc: "How to design a space where focus is the default." }
            ].map((item, i) => (
              <Link key={i} to={item.url} className="block bg-[#F4ECE2] hover:bg-[#F4ECE2] rounded-xl p-4 transition-colors">
                <p className="text-[#2E2A26] text-sm font-medium">{item.title}</p>
                <p className="text-[#2E2A26]/40 text-xs">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* QUIZ */}
      <section className="py-14 px-4 bg-[#F4ECE2]">
        <div className="max-w-xl mx-auto text-center mb-8">
          <p className="text-[#C85A38] text-xs font-medium tracking-[0.2em] uppercase mb-2">30 seconds</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-2">Is Savi right for you?</h2>
          <p className="text-[#2E2A26]/50 text-sm">Four quick questions. No sign-up required.</p>
        </div>
        <SaviQuiz />
      </section>

      {/* FINAL CTA */}
      <section className="py-14 px-4 bg-[#2E2A26]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">Ready to try sober coliving?</h2>
          <p className="text-white/50 text-sm mb-8 max-w-md mx-auto">
            up to 16 residents. Application only. If you want an environment built for clarity, not chaos — take a look.
          </p>
          <Link to="/">
            <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-12 py-5 text-sm font-medium transition-all">
              View Savi Coliving <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
          </Link>
          <p className="text-white/25 text-xs mt-6">savicoliving.com</p>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-8 px-4 bg-[#FBF7F1]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#2E2A26]/30 text-xs uppercase tracking-wider mb-3">Related</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Coliving Valencia</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/quiet-coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Quiet Coliving</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/coliving-valencia-for-founders" className="text-[#C85A38] text-xs hover:underline">Coliving for Founders</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/coworking-valencia" className="text-[#C85A38] text-xs hover:underline">Coworking Valencia</Link>
          </div>
        </div>
      </section>

      <footer className="bg-white py-6 px-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#2E2A26]/40 text-xs">&copy; 2026 SAVI Coliving</p>
          <div className="flex gap-4 text-xs text-[#2E2A26]/40">
            <Link to="/privacy" className="hover:text-[#2E2A26]">Privacy</Link>
            <Link to="/cookies" className="hover:text-[#2E2A26]">Cookies</Link>
            <Link to="/" className="hover:text-[#2E2A26]">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SoberColivingPage;
